import {
  type RunHistoryKeyInfo,
  type RunHistoryKeyType,
} from '../../../types/run';
import {type XAxisValues} from '../../WorkspaceDrawer/Settings/types';
import {runsLinePlotConfigDefaults} from '../defaults';
import {getHasSystemMetrics} from './../common';

type XAxisChoicesArgs = {
  configMetrics: string[];
  configXAxis: XAxisValues | string;
  keyInfo: RunHistoryKeyInfo;
  keyTypes: {
    [key: string]: RunHistoryKeyType;
  };
};

type BaseXAxisChoicesArgs = Omit<XAxisChoicesArgs, 'configXAxis'>;

type BaseXYChoicesArgs = BaseXAxisChoicesArgs & {
  isHistogramAllowed: boolean;
};

function addUniqueString(
  choices: string[],
  seen: Set<string>,
  value: string | undefined
) {
  if (value == null || seen.has(value)) {
    return;
  }
  seen.add(value);
  choices.push(value);
}

function isHistogramMetric(
  configMetrics: string[],
  keyTypes: {[key: string]: RunHistoryKeyType}
) {
  const firstMetric = configMetrics[0];
  return firstMetric != null && keyTypes[firstMetric] === 'histogram';
}

function addDefaultXAxisChoices({
  choices,
  configMetrics,
  seen,
}: {
  choices: string[];
  configMetrics: string[];
  seen: Set<string>;
}) {
  if (!getHasSystemMetrics({metrics: configMetrics})) {
    addUniqueString(choices, seen, '_step');
  }
  addUniqueString(choices, seen, '_absolute_runtime');
  addUniqueString(choices, seen, '_runtime');
  addUniqueString(choices, seen, '_timestamp');
}

export function withSelectedXAxisChoice(
  baseChoices: string[],
  configXAxis: XAxisValues | string | undefined
): string[] {
  if (configXAxis == null) {
    return baseChoices;
  }
  if (baseChoices.includes(configXAxis)) {
    return baseChoices;
  }
  return [...baseChoices, configXAxis];
}

export function getBaseXAxisChoices({
  configMetrics,
  keyInfo,
  keyTypes,
}: BaseXAxisChoicesArgs): string[] {
  const xAxisChoices: string[] = [];
  const seenXAxisChoices = new Set<string>();
  const monotonicChoices: string[] = [];
  const nonMonotonicChoices: string[] = [];
  const excludeNonMonotonicChoices = isHistogramMetric(configMetrics, keyTypes);

  addDefaultXAxisChoices({
    choices: xAxisChoices,
    configMetrics,
    seen: seenXAxisChoices,
  });

  // Limit xAxis choices to number types. Put monotonic choices first, and hide
  // non-monotonic choices for histogram plots.
  for (const k of keyInfo.sortedKeys) {
    if (keyTypes[k] !== 'number') {
      continue;
    }
    if (keyInfo.keys[k].monotonic) {
      monotonicChoices.push(k);
    } else if (!excludeNonMonotonicChoices) {
      nonMonotonicChoices.push(k);
    }
  }

  for (const choice of monotonicChoices) {
    addUniqueString(xAxisChoices, seenXAxisChoices, choice);
  }
  for (const choice of nonMonotonicChoices) {
    addUniqueString(xAxisChoices, seenXAxisChoices, choice);
  }

  return xAxisChoices;
}

export function getXAxisChoices(args: XAxisChoicesArgs): string[] {
  return withSelectedXAxisChoice(getBaseXAxisChoices(args), args.configXAxis);
}

const yAxisNumberKeyBlacklist = new Set(['_absolute_runtime', '_timestamp']);

export function isBlacklistedYAxisNumberKey(k: string): boolean {
  return yAxisNumberKeyBlacklist.has(k);
}

export function getYAxisChoices({
  configMetrics,
  keyInfo,
  keyTypes,
  isHistogramAllowed,
}: {
  configMetrics: string[];
  keyInfo: RunHistoryKeyInfo;
  keyTypes: {[key: string]: RunHistoryKeyType};
  isHistogramAllowed: boolean;
}): string[] {
  const yAxisChoices: string[] = [];
  const seenYAxisChoices = new Set<string>();

  for (const k of keyInfo.sortedKeys) {
    if (keyTypes[k] === 'number') {
      if (!isBlacklistedYAxisNumberKey(k)) {
        addUniqueString(yAxisChoices, seenYAxisChoices, k);
      }
      continue;
    }

    if (keyTypes[k] === 'histogram') {
      // We only allow selecting histograms on single-run pages and reports
      if (isHistogramAllowed) {
        addUniqueString(yAxisChoices, seenYAxisChoices, k);
      }
    }
  }

  // We may have values for the yAxis that are not in yAxisChoices
  // This happens if the user configures the plot, and then changes the query
  // For example on a runs page, visualizes a single run that has one set of metrics,
  // adds a plot, then switch the eyeball to a run with different metrics.
  for (const metric of configMetrics ?? runsLinePlotConfigDefaults.metrics) {
    addUniqueString(yAxisChoices, seenYAxisChoices, metric);
  }

  return yAxisChoices;
}

export function getBaseXYChoices({
  configMetrics,
  keyInfo,
  keyTypes,
  isHistogramAllowed,
}: BaseXYChoicesArgs): {
  baseXAxisChoices: string[];
  yAxisChoices: string[];
} {
  const baseXAxisChoices: string[] = [];
  const seenXAxisChoices = new Set<string>();
  const yAxisChoices: string[] = [];
  const seenYAxisChoices = new Set<string>();
  const monotonicChoices: string[] = [];
  const nonMonotonicChoices: string[] = [];
  const excludeNonMonotonicChoices = isHistogramMetric(configMetrics, keyTypes);

  addDefaultXAxisChoices({
    choices: baseXAxisChoices,
    configMetrics,
    seen: seenXAxisChoices,
  });

  for (const k of keyInfo.sortedKeys) {
    const keyType = keyTypes[k];
    if (keyType === 'number') {
      if (keyInfo.keys[k].monotonic) {
        monotonicChoices.push(k);
      } else if (!excludeNonMonotonicChoices) {
        nonMonotonicChoices.push(k);
      }
      if (!isBlacklistedYAxisNumberKey(k)) {
        addUniqueString(yAxisChoices, seenYAxisChoices, k);
      }
    } else if (keyType === 'histogram' && isHistogramAllowed) {
      addUniqueString(yAxisChoices, seenYAxisChoices, k);
    }
  }

  for (const choice of monotonicChoices) {
    addUniqueString(baseXAxisChoices, seenXAxisChoices, choice);
  }
  for (const choice of nonMonotonicChoices) {
    addUniqueString(baseXAxisChoices, seenXAxisChoices, choice);
  }
  for (const metric of configMetrics ?? runsLinePlotConfigDefaults.metrics) {
    addUniqueString(yAxisChoices, seenYAxisChoices, metric);
  }

  return {baseXAxisChoices, yAxisChoices};
}
